// components/templates/sections/Footer.tsx
'use client';
import Image from 'next/image';
import Link from 'next/link';
import { useDesign } from '@/context/DesignContext';

interface SocialLink {
  icon: string;
  url: string;
}

interface FooterLocation {
  address: string;
  email: string;
  phone: string;
}

interface FooterProps {
  logo: string;
  description: string;
  socialLinks?: SocialLink[];
  openingHours?: string[];
  location: FooterLocation;
  newsletterPlaceholder?: string;
  privacyLabel?: string;
  design?: any;
}

export default function Footer({
  logo,
  description,
  socialLinks = [],
  openingHours = [],
  location,
  newsletterPlaceholder = 'Email *',
  privacyLabel = 'I agree to the Privacy Policy',
  design: propDesign,
}: FooterProps) {
  const contextDesign = useDesign();
  const design = propDesign || contextDesign;

  const socialArray = Array.isArray(socialLinks) ? socialLinks : [];
  const hoursArray = Array.isArray(openingHours) ? openingHours : [];

  const bgColor = design?.colors?.secondary || '#3D405B';
  const textColor = design?.colors?.textLight || '#6C757D';
  const primaryColor = design?.colors?.primary || '#E07A5F';
  const headingFont = design?.typography?.headingFont || "'Sora', sans-serif";
  const bodyFont = design?.typography?.bodyFont || "'Plus Jakarta Sans', sans-serif";
  const btnPrimary = design?.buttons?.primary || {
    class: 'btn btn-primary',
    backgroundColor: 'var(--primary)',
    textColor: '#FFFFFF',
    borderRadius: '30px',
    padding: '12px 30px',
  };
  const containerMaxWidth = design?.spacing?.containerMaxWidth || '1200px';

  const footerStyle: React.CSSProperties = {
    backgroundColor: bgColor,
    color: textColor,
    padding: '60px 0 30px',
    fontFamily: bodyFont,
  };
  const linkHoverStyle = { color: primaryColor };
  const socialIconStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    border: `1px solid ${primaryColor}`,
    color: primaryColor,
    marginRight: '0.5rem',
    transition: 'all 0.3s',
  };
  const buttonStyle: React.CSSProperties = {
    backgroundColor: btnPrimary.backgroundColor,
    color: btnPrimary.textColor,
    padding: btnPrimary.padding,
    borderRadius: btnPrimary.borderRadius,
    border: 'none',
    cursor: 'pointer',
  };

  return (
    <footer className="footer" style={footerStyle}>
      <div className="container" style={{ maxWidth: containerMaxWidth, margin: '0 auto' }}>
        <div className="row" style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem' }}>
          <div className="col-lg-3 col-md-6">
            <div className="widget logo">
              <Image src={logo} alt="logo" width={140} height={40} style={{ marginBottom: '1rem' }} />
              <p>{description}</p>
              {socialArray.length > 0 && (
                <ul className="list-social" style={{ display: 'flex', listStyle: 'none', padding: 0, marginTop: '1rem' }}>
                  {socialArray.map((link, idx) => (
                    <li key={idx}>
                      <a href={link.url} style={socialIconStyle}>
                        <i className={link.icon}></i>
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div className="col-lg-3 col-md-6">
            <div className="widget time">
              <h5 style={{ fontFamily: headingFont, color: '#fff', marginBottom: '1rem' }}>opening time</h5>
              {hoursArray.length > 0 ? (
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {hoursArray.map((hour, idx) => (
                    <li key={idx}>{hour}</li>
                  ))}
                </ul>
              ) : (
                <p>Horaires non disponibles</p>
              )}
            </div>
          </div>
          <div className="col-lg-3 col-md-6">
            <div className="widget locations">
              <h5 style={{ fontFamily: headingFont, color: '#fff', marginBottom: '1rem' }}>location</h5>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                <li>
                  <span style={{ fontWeight: 'bold' }}>Address:</span>
                  <p>{location?.address || 'Adresse non disponible'}</p>
                </li>
                <li>
                  <span style={{ fontWeight: 'bold' }}>Booking & Contact:</span>
                  <p><a href={`mailto:${location?.email}`} style={{ color: textColor, textDecoration: 'none' }}>{location?.email || 'Email non disponible'}</a></p>
                  <p>{location?.phone || 'Téléphone non disponible'}</p>
                </li>
              </ul>
            </div>
          </div>
          <div className="col-lg-3 col-md-6">
            <div className="widget latestnew">
              <h5 style={{ fontFamily: headingFont, color: '#fff', marginBottom: '1rem' }}>latest new</h5>
              <p>Register your email to not miss any news and offers from us!</p>
              <form>
                <div className="form-group">
                  <input
                    type="email"
                    className="form-control"
                    placeholder={newsletterPlaceholder}
                    style={{ width: '100%', padding: '0.5rem', marginBottom: '0.5rem', borderRadius: '4px', border: 'none' }}
                  />
                </div>
                <div className="form-check" style={{ marginBottom: '0.5rem' }}>
                  <input type="checkbox" className="form-check-input" id="privacyCheck" />
                  <label className="form-check-label" htmlFor="privacyCheck" style={{ marginLeft: '0.25rem' }}>{privacyLabel}</label>
                </div>
                <button type="submit" className={btnPrimary.class} style={buttonStyle}>
                  <i className="fa fa-paper-plane"></i>
                </button>
              </form>
            </div>
          </div>
        </div>
        <div className="row bottom-footer" style={{ borderTop: '1px solid rgba(255,255,255,0.1)', marginTop: '2rem', paddingTop: '1rem', textAlign: 'center' }}>
          <div className="bottom-main">
            <p>Copyright © 2023 Themesflat. All Rights Reserved.</p>
            <ul style={{ display: 'flex', justifyContent: 'center', gap: '1rem', listStyle: 'none', padding: 0 }}>
              <li><a href="#" style={{ color: textColor, textDecoration: 'none' }}>PRIVACY</a></li>
              <li><a href="#" style={{ color: textColor, textDecoration: 'none' }}>TERM OF USE</a></li>
              <li><a href="#" style={{ color: textColor, textDecoration: 'none' }}>POLICY</a></li>
            </ul>
          </div>
        </div>
      </div>
    </footer>
  );
}